import { CaseDetails, CurrentPension, Employed, FuturePension, Applicant } from '../../../CaseDetailsPage.types';
import { Current } from 'modules/cm/domain/Case/Applicant/Income/Current/Current';
import { Future } from 'modules/cm/domain/Case/Applicant/Income/Future/Future';
import { FIELDS } from 'modules/cm/fields/types';
import {
  emailValidation,
  nullableNumber,
  nullableString,
  postcodeValidation,
  telephoneValidation,
} from 'utils/form/validation';
import * as yup from 'yup';

export default yup
  .object()
  .shape<
    Omit<
      Applicant,
      | 'id'
      | 'commitmentToBeCleared'
      | 'commitmentRemaining'
      | FIELDS.ASSETS_PREFIX
      | FIELDS.EXPENDITURES_PREFIX
      | keyof CurrentPension
      | keyof FuturePension
      | keyof Employed
      | keyof Current
      | keyof Future
      | FIELDS.APPLICANT_NUMBER_OF_DEPENDANTS
    >
  >({
    [FIELDS.APPLICANT_TITLE]: yup.string(),
    [FIELDS.APPLICANT_NAME]: yup.string(),
    [FIELDS.APPLICANT_SURNAME]: yup.string(),
    [FIELDS.APPLICANT_MIDDLE_NAME]: nullableString(),
    [FIELDS.APPLICANT_PREVIOUS_NAME]: nullableString(),
    [FIELDS.APPLICANT_PROOF_OF_ID]: nullableString(),
    [FIELDS.APPLICANT_DATE_OF_BIRTH]: nullableString(),
    [FIELDS.APPLICANT_GENDER]: nullableString(),
    [FIELDS.APPLICANT_MARITAL_STATUS]: nullableString(),
    [FIELDS.APPLICANT_CURRENT_ADDRESS]: yup.object().shape<CaseDetails['case']['applicants'][0]['currentAddress']>({
      id: yup.number(),
      [FIELDS.APPLICANT_ADDRESS_FLAT_NUMBER]: nullableString(),
      [FIELDS.APPLICANT_ADDRESS_BUILDING_NAME]: nullableString(),
      [FIELDS.APPLICANT_ADDRESS_BUILDING_NUMBER]: nullableString(),
      [FIELDS.APPLICANT_ADDRESS_STREET]: nullableString(),
      [FIELDS.APPLICANT_ADDRESS_CITY]: nullableString(),
      [FIELDS.APPLICANT_ADDRESS_POSTCODE]: nullableString(),
      [FIELDS.APPLICANT_ADDRESS_PROOF_OF_ADDRESS]: nullableString(),
      [FIELDS.APPLICANT_ADDRESS_MOVED_IN_DATE]: nullableString(),
    }),
    [FIELDS.APPLICANT_PREVIOUS_ADDRESSES]: yup.array().of(
      yup.object().shape<CaseDetails['case']['applicants'][0]['previousAddresses'][0]>({
        id: yup.number(),
        [FIELDS.APPLICANT_ADDRESS_FLAT_NUMBER]: nullableString(),
        [FIELDS.APPLICANT_ADDRESS_BUILDING_NAME]: nullableString(),
        [FIELDS.APPLICANT_ADDRESS_BUILDING_NUMBER]: nullableString(),
        [FIELDS.APPLICANT_ADDRESS_STREET]: nullableString(),
        [FIELDS.APPLICANT_ADDRESS_CITY]: nullableString(),
        [FIELDS.APPLICANT_ADDRESS_POSTCODE]: nullableString(),
        [FIELDS.APPLICANT_ADDRESS_MOVED_IN_DATE]: nullableString(),
        [FIELDS.APPLICANT_ADDRESS_PROOF_OF_ADDRESS]: nullableString(),
      }),
    ),
    [FIELDS.APPLICANT_POWER_OF_ATTORNEY]: yup.object().shape<CaseDetails['case']['applicants'][0]['powerOfAttorney']>({
      id: yup.number(),
      [FIELDS.APPLICANT_POWER_OF_ATTORNEY_NAME]: nullableString(),
      [FIELDS.APPLICANT_POWER_OF_ATTORNEY_HAS_CUSTOMISED_SERVICES]: yup.boolean().nullable(),
      [FIELDS.APPLICANT_POWER_OF_ATTORNEY_CUSTOMISED_SERVICES_REQUEST]: nullableString(),
      [FIELDS.APPLICANT_POWER_OF_ATTORNEY_PHONE_NUMBER]: telephoneValidation().nullable(),
      [FIELDS.APPLICANT_POWER_OF_ATTORNEY_EMAIL]: emailValidation().nullable(),
      [FIELDS.APPLICANT_POWER_OF_ATTORNEY_POSTCODE]: postcodeValidation().nullable(),
      [FIELDS.APPLICANT_POWER_OF_ATTORNEY_FLAT_NUMBER]: nullableString(),
      [FIELDS.APPLICANT_POWER_OF_ATTORNEY_BUILDING_NAME]: nullableString(),
      [FIELDS.APPLICANT_POWER_OF_ATTORNEY_BUILDING_NUMBER]: nullableString(),
      [FIELDS.APPLICANT_POWER_OF_ATTORNEY_STREET]: nullableString(),
      [FIELDS.APPLICANT_POWER_OF_ATTORNEY_CITY]: nullableString(),
    }),
    [FIELDS.APPLICANT_NATIONALITY]: nullableString(),
    [FIELDS.APPLICANT_IS_UK_RESIDENT]: yup.boolean().nullable(),
    [FIELDS.APPLICANT_HAS_INDEFINITE_LEAVE_TO_REMAIN]: yup.boolean().nullable(),
    [FIELDS.APPLICANT_PHONE_HOME]: telephoneValidation('home').nullable(),
    [FIELDS.APPLICANT_IS_PHONE_HOME_PEPPER]: yup.boolean().nullable(),
    [FIELDS.APPLICANT_PHONE_MOBILE]: telephoneValidation('mobile').nullable(),
    [FIELDS.APPLICANT_IS_PHONE_MOBILE_PEPPER]: yup.boolean().nullable(),
    [FIELDS.APPLICANT_PHONE_WORK]: telephoneValidation('work').nullable(),
    [FIELDS.APPLICANT_IS_PHONE_WORK_PEPPER]: yup.boolean().nullable(),
    [FIELDS.APPLICANT_EMAIL]: emailValidation('Email Address'),
    [FIELDS.APPLICANT_IS_EMAIL_PEPPER]: yup.boolean().nullable(),
    [FIELDS.APPLICANT_ADDITIONAL_EMAIL]: nullableString(),
    [FIELDS.APPLICANT_ADDITIONAL_EMAIL_PEPPER]: yup.boolean().nullable(),
    [FIELDS.APPLICANT_IS_CRIMINAL]: nullableString(),
    [FIELDS.APPLICANT_CRIMINAL_CONVICTIONS_DETAIL]: nullableString(),
    [FIELDS.APPLICANT_HAS_ADVERSE_CREDIT]: nullableString(),
    [FIELDS.APPLICANT_ADVERSE_CREDIT_DETAILS]: nullableString(),
    [FIELDS.APPLICANT_BANKRUPTCY_AMOUNT]: nullableNumber(),
    [FIELDS.APPLICANT_BANKRUPTCY_LATEST_DATE]: nullableString(),
    [FIELDS.APPLICANT_CCJ_AMOUNT]: nullableNumber(),
    [FIELDS.APPLICANT_CCJ_LATEST_DATE]: nullableString(),
    [FIELDS.APPLICANT_DEFAULT_AMOUNT]: nullableNumber(),
    [FIELDS.APPLICANT_DEFAULT_LAST_DATE]: nullableString(),
    [FIELDS.APPLICANT_MORTGAGE_ARREARS_AMOUNT]: nullableNumber(),
    [FIELDS.APPLICANT_MORTGAGE_ARREARS_LATEST_DATE]: nullableString(),
  });
